// frontend/src/components/profile/AccountSettings.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { FaLock, FaTrash, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { authApi } from '../../api/auth'; // <--- IMPORT authApi

function AccountSettings() {
  const { user, logout } = useAuth();
  const { addToast } = useToast();
  
  // Password State
  const [passwords, setPasswords] = useState({
      current_password: '',
      new_password: '',
      confirm_new_password: ''
  });
  const [loadingPass, setLoadingPass] = useState(false);

  // Delete State
  const [isDeleting, setIsDeleting] = useState(false);

  // --- PASSWORD HANDLERS ---
  const handlePassChange = (e) => {
      setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const handleUpdatePassword = async (e) => {
      e.preventDefault();
      if (user?.email.endsWith('@phone.user')) {
          addToast("Phone users must reset their password via the login screen's 'Forgot Password' link.", "error");
          return;
      }
      
      if (passwords.new_password !== passwords.confirm_new_password) {
          addToast("New passwords do not match.", "error");
          return;
      }
      if (passwords.new_password.length < 6) {
          addToast("Password must be at least 6 characters.", "error");
          return;
      }

      setLoadingPass(true);
      try {
          // The authApi.changePassword handles the token internally
          await authApi.changePassword(passwords); 
          addToast("Password updated successfully! Logging out...", "success");
          setPasswords({ current_password: '', new_password: '', confirm_new_password: '' });
          setTimeout(async () => {
              await logout();
          }, 3000);
      } catch (err) {
          addToast(err.message || "Failed to update password.", "error");
      } finally {
          setLoadingPass(false);
      }
  };

  // --- DELETE HANDLER (FIXED) ---
  const handleDeleteAccount = async () => {
      if (!window.confirm("⚠️ ARE YOU SURE? This will permanently delete your account, order history, and garage data. This action cannot be undone.")) {
          return;
      }
      
      // Double confirmation
      const confirmation = prompt("Type 'DELETE' to confirm account deletion:");
      if (confirmation !== 'DELETE') return;

      setIsDeleting(true);
      try {
          // --- CRITICAL FIX: Call the API endpoint ---
          await authApi.deleteAccount(); 
          // ------------------------------------------
          
          addToast("Account deleted. Goodbye!", "info");
          logout(); // Log them out immediately
      } catch (err) {
          addToast(err.message || "Failed to delete account.", "error");
          setIsDeleting(false);
      }
  };

  return (
    <div className="space-y-8 animate-fadeIn">
        
        {/* 1. SECURITY SECTION */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaLock className="text-blue-600" /> Security
            </h3>
            
            <form onSubmit={handleUpdatePassword} className="space-y-4 max-w-md">
                {user?.email.endsWith('@phone.user') && (
                    <div className="bg-yellow-50 text-yellow-800 p-3 rounded text-xs font-bold flex items-center gap-2">
                        <FaExclamationTriangle /> Phone users must use the "Forgot Password" link on the login screen to reset.
                    </div>
                )}
                
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Current Password</label>
                    <input 
                        type="password" name="current_password" 
                        value={passwords.current_password} onChange={handlePassChange}
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        required
                        disabled={user?.email.endsWith('@phone.user')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">New Password</label>
                    <input 
                        type="password" name="new_password" 
                        value={passwords.new_password} onChange={handlePassChange}
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        required
                        disabled={user?.email.endsWith('@phone.user')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Confirm New Password</label>
                    <input 
                        type="password" name="confirm_new_password" 
                        value={passwords.confirm_new_password} onChange={handlePassChange}
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        required
                        disabled={user?.email.endsWith('@phone.user')}
                    />
                </div>
                <button 
                    type="submit" 
                    disabled={loadingPass || user?.email.endsWith('@phone.user')}
                    className="bg-blue-600 text-white px-4 py-2 rounded font-bold text-sm hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                >
                    {loadingPass ? <FaSpinner className="animate-spin" /> : "Update Password"}
                </button>
            </form>
        </div>

        {/* 2. DANGER ZONE */}
        <div className="bg-red-50 p-6 rounded-lg border border-red-200">
            <h3 className="text-lg font-bold text-red-800 mb-2 flex items-center gap-2">
                <FaExclamationTriangle /> Danger Zone
            </h3>
            <p className="text-sm text-red-700 mb-4">
                Once you delete your account, there is no going back. This will wipe all your data, orders, and garage history.
            </p>
            <button 
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="bg-red-600 text-white px-4 py-2 rounded font-bold text-sm hover:bg-red-700 flex items-center gap-2 disabled:opacity-50"
            >
                {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} Delete Account
            </button>
        </div>

    </div>
  );
}

export default AccountSettings;